import { useState, useEffect, useRef } from "react"
import { useLocation } from "wouter"

const PASSWORD = "ricetseng770820"
const SESSION_KEY = "case_authed"

const cases = {
  okx: {
    title: "OKX Web3 Growth",
    tags: ["Web3", "0→1"],
    year: "2024",
    // An array, so the two halves stay separate paragraphs — the first
    // is the positioning line, the second the detail. Run together they
    // read as one dense block.
    description: [
      "Leading the vision, crafting the concepts, and scaling the team behind OKX Web3 Growth.",
      "I work hands-on from 0→1 product definition and concept exploration to experience craft and iteration, across Prediction Market, Boost, on-chain Discovery, DEX Affiliate, Smart Money.",
    ],
    decks: [
      {
        label: "Prediction Market",
        href: "https://www.figma.com/deck/4700aKJZpwwEwDleGEJsHu",
        // Rendered filled rather than outlined. With two decks side by
        // side and equal weight, neither reads as the thing to open
        // first — this makes the lead project unambiguous.
        primary: true,
      },
      { label: "Boost", href: "https://www.figma.com/deck/U8thJ7uxplBEDJbS7Brp7e" },
    ],
  },
  defi: {
    title: "DeFi Earn Experience",
    tags: ["DeFi", "Redesign"],
    year: "2024",
    description: "A comprehensive redesign of the DeFi Earn product at OKX — making the investing experience simpler, more trustworthy, and accessible to a broader audience. The redesign focused on reducing friction in the earn flow, improving information clarity around yield and risk, and creating a more cohesive experience across staking, lending, and liquidity provision.",
    decks: [
      { label: "DeFi Earn", href: "https://www.figma.com/deck/Pq93GkYrHMFIiO9WiJ5RX5" },
    ],
  },
  grab: {
    title: "Pay with Grab",
    tags: ["FinTech", "Strategy"],
    year: "2020",
    description: "Revolutionizing the payment experience for online and offline payments across Southeast Asia — a 2-year strategic initiative covering payment onboarding, financial product activation, and a unified design system built to scale across 8 markets.",
    decks: [],
  },
}

// Scroll reveal hook
function useReveal(threshold = 0.15) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect() }
    }, { threshold })
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, visible]
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal()
  return (
    <div
      ref={ref}
      className={`grab-reveal ${visible ? "grab-reveal--in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

// HMW section — staggered text entrance, no border lines
function HMWSection() {
  const [ref, visible] = useReveal(0.3)
  return (
    <div ref={ref} className={`grab-hmw ${visible ? "grab-hmw--in" : ""}`}>
      <div className="grab-hmw-label">How Might We</div>
      <blockquote className="grab-hmw-quote">
        "HMW define a simple, connected, flexible payment experience while minimising cost of funds that allows Grab to be the most rewarding, secure and convenient payment in SEA"
      </blockquote>
    </div>
  )
}

// SVG Donut chart
function DonutChart({ spendPct, size = 144, visible, delay = 0 }) {
  const strokeW = 22
  const r = (size - strokeW) / 2
  const circ = 2 * Math.PI * r
  const gap = 7
  const spendArc = (spendPct / 100) * circ - gap / 2
  const growArc = (1 - spendPct / 100) * circ - gap / 2
  return (
    <svg
      width={size} height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{
        transform: "rotate(-90deg)",
        opacity: visible ? 1 : 0,
        scale: visible ? 1 : 0.88,
        transition: `opacity 0.7s ${delay}s ease, scale 0.7s ${delay}s cubic-bezier(0.16,1,0.3,1)`,
      }}
    >
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border-strong)" strokeWidth={strokeW} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#2b5c2e" strokeWidth={strokeW}
        strokeDasharray={`${spendArc} ${circ}`} strokeDashoffset={0} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#6ab04c" strokeWidth={strokeW}
        strokeDasharray={`${growArc} ${circ}`} strokeDashoffset={-(spendArc + gap)} />
    </svg>
  )
}

// Target Customer Segment section
function TargetSegments() {
  const [ref, visible] = useReveal(0.15)
  const segments = [
    {
      category: "Young white collar",
      access: "Future access",
      age: "18 – 24 y.o.",
      desc: "Looking to afford new lifestyle and also save money from the beginning. Open to trying out new financial products as well but don't know where to start from.",
      spendPct: 65,
    },
    {
      category: "Young white collar",
      access: "Future access (SG, MY, VN, PH)\nAccess (PH only)",
      age: "25 – 34 y.o.",
      desc: "Constantly trying to maximise finances as money is required in future planning. Need to keep a good credit score to get loan in future.",
      spendPct: 52,
    },
    {
      category: "Family",
      access: "Limited / no access",
      age: "≥ 35 y.o.",
      desc: "Family is priority. Wants to fulfill all their needs, help them grow and provide them financial security. Faces difficulties in finding the right financial product for each of their needs.",
      spendPct: 44,
    },
  ]
  return (
    <div ref={ref} className={`grab-segments ${visible ? "grab-segments--in" : ""}`}>
      <div className="grab-segments-cols">
        {segments.map((s, i) => (
          <div className="grab-segment-col" key={i} style={{ "--i": i }}>
            <div className="grab-segment-donut">
              <DonutChart spendPct={s.spendPct} visible={visible} delay={i * 0.13} />
            </div>
            <div className="grab-segment-category">{s.category}</div>
            <div className="grab-segment-access">{s.access}</div>
            <div className="grab-segment-age">{s.age}</div>
            <p className="grab-segment-desc">{s.desc}</p>
          </div>
        ))}
      </div>
      <div className="grab-segments-legend">
        <div className="grab-seg-legend-item">
          <div className="grab-seg-legend-dot" style={{ background: "#2b5c2e" }} />
          <span>Spend &amp; Earn</span>
        </div>
        <div className="grab-seg-legend-item">
          <div className="grab-seg-legend-dot" style={{ background: "#6ab04c" }} />
          <span>Grow &amp; Protect</span>
        </div>
      </div>
    </div>
  )
}

// Lightbox
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose])
  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <img
        src={src}
        alt=""
        className="lightbox-img"
        onClick={(e) => e.stopPropagation()}
      />
      <button className="lightbox-close" onClick={onClose}>✕</button>
    </div>
  )
}

// Customer Friction Index — journey stage visualization
const sImg = (n) => n === 1
  ? "/Pay%20with%20Grab/image%201png.png"
  : `/Pay%20with%20Grab/image%20${n}.png`

function ProblemViz() {
  const [ref, visible] = useReveal(0.15)
  const [lightbox, setLightbox] = useState(null)

  const stages = [
    {
      emoji: "🚢", stage: "Onboarding",
      images: [sImg(1)],
      problems: [
        { title: "High drop-off rate", body: "31% drop-off from the activation home" }
      ]
    },
    {
      emoji: "🎉", stage: "Activation",
      images: [sImg(2)],
      problems: [
        { title: "Lack of clarity", body: "Time consuming activation processes." }
      ]
    },
    {
      emoji: "💳", stage: "Making payment",
      images: [sImg(3), sImg(4)],
      problems: [
        { title: "Consistency", body: "inconsistent online & offline payment experience" },
        { title: "Scalability", body: "can't scale while enabling more payment option" }
      ]
    },
    {
      emoji: "🌟", stage: "Post payment",
      images: [sImg(5)],
      problems: [
        { title: "Rewarding", body: "User doesn't feel rewarding in post-payment journey" }
      ]
    },
  ]

  return (
    <>
      <div ref={ref} className={`grab-problem-viz ${visible ? "grab-problem-viz--in" : ""}`}>
        <div className="grab-viz-header">
          <span className="grab-viz-title">Customer Friction Index</span>
          <span className="grab-viz-caption">Pain points across the payment journey</span>
        </div>
        <div className="grab-viz-stages">
          {stages.map((s, i) => (
            <div
              className={`grab-viz-stage${s.images.length > 1 ? " grab-viz-stage--wide" : ""}`}
              key={s.stage}
              style={{ "--i": i }}
            >
              {/* Stage header */}
              <div className="grab-viz-stage-header">
                <span className="grab-viz-stage-emoji">{s.emoji}</span>
                <span className="grab-viz-stage-name">{s.stage}</span>
              </div>
              {/* Images */}
              <div className={`grab-viz-imgs${s.images.length > 1 ? " grab-viz-imgs--two" : ""}`}>
                {s.images.map((src, j) => (
                  <div className="grab-viz-img-wrap" key={j} onClick={() => setLightbox(src)}>
                    <img src={src} alt={`${s.stage} ${j + 1}`} />
                    <div className="grab-viz-zoom-hint">↗</div>
                  </div>
                ))}
              </div>
              {/* Problems */}
              <div className="grab-viz-problems">
                {s.problems.map((p, j) => (
                  <div className="grab-viz-problem" key={j}>
                    <div className="grab-viz-problem-title">{p.title}</div>
                    <div className="grab-viz-problem-body">{p.body}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </>
  )
}

export default function CaseStudy({ id }) {
  const [, navigate] = useLocation()
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(SESSION_KEY) === "1")
  const [input, setInput] = useState("")
  const [error, setError] = useState(false)

  const data = cases[id]

  useEffect(() => {
    if (!data) navigate("/work", { replace: true })
  }, [data, navigate])

  if (!data) return null

  function handleSubmit(e) {
    e.preventDefault()
    if (input === PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, "1")
      setAuthed(true)
    } else {
      setError(true)
      setInput("")
    }
  }

  if (!authed) {
    return (
      <div className="page-panel">
        <div className="case-gate">
          <div className="case-gate-inner">
            <div className="panel-tag" style={{ marginBottom: 24 }}>Password Protected</div>
            <h2 className="case-gate-title">Enter<br />Password</h2>
            <p className="case-gate-subtitle">This case study is for authorised viewers only.</p>
            <form onSubmit={handleSubmit} className="case-gate-form">
              <input
                type="password"
                value={input}
                onChange={(e) => { setInput(e.target.value); setError(false) }}
                placeholder="Password"
                className={`case-gate-input${error ? " is-error" : ""}`}
                autoFocus
              />
              {error && <p className="case-gate-error">Incorrect password. Try again.</p>}
              <button type="submit" className="case-gate-btn">Enter →</button>
            </form>
            <button className="back-btn" style={{ marginTop: 40 }} onClick={() => navigate("/work")}>← Back to Work</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-panel">
      <div className="panel-inner">
        <div className="panel-header">
          <div className="panel-tag">
            <button className="back-btn" onClick={() => navigate("/work")}>← Work</button>
          </div>
          <h2 className="panel-title">{data.title}</h2>
        </div>

        <div className="case-body">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
            {data.tags.map(t => <span key={t} className="work-tag">{t}</span>)}
            <span className="work-tag">{data.year}</span>
          </div>
          {/* description is either a string or an array of paragraphs;
              normalised here so both shapes render, rather than forcing
              every case to be rewritten as an array. */}
          {(Array.isArray(data.description) ? data.description : [data.description]).map((para) => (
            <p key={para} className="case-description">{para}</p>
          ))}
          {/* A case can carry more than one deck — OKX has both Prediction
              Market and Boost — so each is named rather than sharing a
              generic "View Deck", which would give no way to tell them
              apart. */}
          {data.decks?.length > 0 && (
            <div className="case-deck-row">
              {data.decks.map((d) => (
                <a
                  key={d.href}
                  href={d.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`case-deck-btn${d.primary ? " case-deck-btn--primary" : ""}`}
                >
                  {d.label} deck ↗
                </a>
              ))}
            </div>
          )}
        </div>

        {id === "grab" && <GrabContent />}
      </div>
    </div>
  )
}

const img = (n, ext = "png") => `/Pay%20with%20Grab/intro${String(n).padStart(2, "0")}.${ext}`

function ResultsSection() {
  const [ref, visible] = useReveal(0.1)
  return (
    <div ref={ref} className={`grab-results-grid ${visible ? "grab-results--in" : ""}`}>
      {/* Hero — dark, spans 2 rows */}
      <div className="grab-result-hero" style={{ "--i": 0 }}>
        <div className="grab-result-hero-num">100+</div>
        <div className="grab-result-hero-desc">Design jam with 15+ designers and came out 20+ PWG patterns</div>
      </div>
      {/* Mid cards — auto-place into cols 2–3 */}
      <div className="grab-result-card" style={{ "--i": 1 }}>
        <div className="grab-result-num">+102%</div>
        <div className="grab-result-label">ROI — return on investment massively uplifts</div>
      </div>
      <div className="grab-result-card" style={{ "--i": 2 }}>
        <div className="grab-result-num">~9.6%</div>
        <div className="grab-result-label">AOV — avg order value uplifts (PLOFF+PLOA+ATOM)</div>
      </div>
      {/* Side — col 4, spans 2 rows */}
      <div className="grab-result-side" style={{ "--i": 3 }}>
        <div className="grab-result-side-num">3.6%</div>
        <div className="grab-result-side-label">significant increase in Activation Conversion Rate</div>
      </div>
      {/* Row 2 mid cards */}
      <div className="grab-result-card" style={{ "--i": 4 }}>
        <div className="grab-result-num">1.3–1.4x</div>
        <div className="grab-result-label">increased the task completion rate across Full KYC, GrabFin, add card</div>
      </div>
      <div className="grab-result-card" style={{ "--i": 5 }}>
        <div className="grab-result-num">~19%</div>
        <div className="grab-result-label">Potential Loyal user has a relatively high engagement rate</div>
      </div>
    </div>
  )
}

function GrabContent() {
  return (
    <div className="grab-case">

      {/* ── Hero ── */}
      <Reveal>
        <div className="grab-hero">
          <img src={img(1)} alt="Pay with Grab hero" />
        </div>
      </Reveal>

      {/* ── Overview meta ── */}
      <Reveal>
        <div className="grab-meta-row">
          <div className="grab-meta-item">
            <span className="grab-meta-label">Role</span>
            <span className="grab-meta-value">Principal Product Designer</span>
          </div>
          <div className="grab-meta-item">
            <span className="grab-meta-label">Responsibilities</span>
            <span className="grab-meta-value">Leading Pay with Grab vision — Pay offline, GrabPay card, gamified payment, Pay SDK, PayLater, payment strategy & unified patterns</span>
          </div>
          <div className="grab-meta-item">
            <span className="grab-meta-label">Team</span>
            <span className="grab-meta-value">10+ payment designers · 2-year strategy</span>
          </div>
          <div className="grab-meta-item">
            <span className="grab-meta-label">Impact</span>
            <span className="grab-meta-value">8 Markets · Millions of Transactions</span>
          </div>
        </div>
      </Reveal>

      {/* ── Challenges + Team ── */}
      <Reveal>
        <div className="grab-3col-info">
          <div className="grab-info-col">
            <div className="grab-info-label">Responsibilities</div>
            <p className="grab-info-text">Leading 'Pay with Grab' vision initiative, includes Pay offline, GrabPay card, gamified payment, pay SDK, PayLater experience, Drafting the payment strategy document, & unifying all 'Pay with Grab' patterns.</p>
          </div>
          <div className="grab-info-col">
            <div className="grab-info-label">Challenges</div>
            <ul className="grab-info-list">
              <li>Mentorship: Strengthening working relationships among designers</li>
              <li>Managing senior leadership stakeholders</li>
              <li>Tech constraint: unified the experience that could be scaled and easy to build</li>
            </ul>
          </div>
          <div className="grab-info-col">
            <div className="grab-info-label">Team & Timeline</div>
            <ul className="grab-info-list">
              <li>Work with 10+ payment designers to craft Pay with Grab vision work</li>
              <li>2-year long term strategy and implementation</li>
            </ul>
          </div>
        </div>
      </Reveal>

      {/* ── Divider ── */}
      <Reveal><div className="grab-divider" /></Reveal>

      {/* ── Background ── */}
      <Reveal>
        <div className="grab-section-label">Background</div>
      </Reveal>
      <Reveal delay={80}>
        <div className="grab-2col-layout">
          <div>
            <p className="grab-body-text">In 2020, I joined Grab Financial Group as the Consumer Payment Experience Product Design Manager. I saw a lot of broken experiences on the payment side and never got a chance to pick it up.</p>
            <p className="grab-body-text">In 2021, I changed back to an individual contributor and had the opportunity to participate in a strategic initiative project to build it from the ground up. The objective was to revolutionize the end-to-end payment experience, including discovery and activation, payment processing, and post-payment services.</p>
            <p className="grab-body-text">Our users always had confusion about different payment journeys — "What's the difference between the payment methods?", "What is postpaid and why is it now my default payment method?" They also lacked clarity on when to use which product.</p>
          </div>
          <div className="grab-quotes">
            <div className="grab-quote">
              "What's the difference between the payment methods, what is postpaid and why is it now my default payment method?"
            </div>
            <div className="grab-quote">
              <div>"I have no idea when should I use what product? Or what's the differences between them"</div>
              <div style={{ marginTop: 12 }}>"Paying online look so different from paying offline, can you apply the same offer to it?"</div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* ── Full image ── */}
      <Reveal>
        <div className="grab-full-img">
          <img src={img(2)} alt="Context overview" />
        </div>
      </Reveal>

      {/* ── Top 4 problem spaces ── */}
      <Reveal><div className="grab-section-label">Top 4 Customer Problem Spaces</div></Reveal>
      <Reveal delay={80}>
        <div className="grab-problems-grid">
          {[
            { title: "Inconsistent", body: "Inconsistent check-out flow for different products like P2M (offline), Online and GAAP services." },
            { title: "Unaware of payment offering", body: "Users are unaware of the value of each payment method and are deterred by lengthy activation and confusing rewards." },
            { title: "Doesn't feel rewarding", body: "Not incentivising and nudging consumers with promos, discounts, cash backs and rewards at the right moment. Poor visibility of promos/rewards." },
            { title: "Complex", body: "Time consuming payment activation processes, confusing value props, complicated T&Cs." },
          ].map((p, i) => (
            <Reveal key={i} delay={i * 60}>
              <div className="grab-problem-card">
                <div className="grab-problem-num">0{i + 1}</div>
                <div className="grab-problem-title">{p.title}</div>
                <div className="grab-problem-body">{p.body}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Reveal>

      {/* ── Problem viz (replaces journey image) ── */}
      <Reveal>
        <ProblemViz />
      </Reveal>

      {/* ── HMW ── */}
      <HMWSection />

      {/* ── Workshop ── */}
      <Reveal><div className="grab-section-label">How We Got Started</div></Reveal>
      <Reveal delay={80}>
        <div className="grab-2col-layout">
          <div>
            <h3 className="grab-h3">A cross-functional workshop to align vision</h3>
            <p className="grab-body-text">We commenced by bringing together various functional teams, including business leaders, product leaders, design professionals, and PMMs. We engaged in discussions covering GrabFin updates, the 2-year payment strategy, user insights, marketing insights, and our present status.</p>
            <p className="grab-body-text">Through a series of activities, we reviewed and revised the vision, metrics, and target segments — and engaged in ideation exercises such as user journey mapping and general ideation.</p>
            <p className="grab-body-text">As a result, we formulated a fresh vision, refined metrics, enhanced Customer Value Proposition (CVP), and generated a list of innovative ideas to propel the initiative forward.</p>
          </div>
          <div className="grab-gif-frame">
            <img src={img(5, "gif")} alt="Workshop in action" />
          </div>
        </div>
      </Reveal>

      <Reveal><div className="grab-divider" /></Reveal>

      {/* ── Prototyping & Research ── */}
      <Reveal><div className="grab-section-label">Prototyping & Research</div></Reveal>
      <Reveal delay={80}>
        <p className="grab-body-text grab-body-text--wide">To comprehensively cover the entire payment journey, I crafted a series of interactive prototypes to evaluate various concepts including "Financial Product Activation," "In-Store Payment Experience," and "Gamified payment experience." Collaborating with diverse design professionals from different teams, my role involved steering all payment-related items toward the unified "Pay with Grab" vision.</p>
      </Reveal>
      <Reveal><div className="grab-divider" /></Reveal>

      {/* ── Target Customer Segments ── */}
      <Reveal><div className="grab-section-label">Target Customer Segments</div></Reveal>
      <Reveal delay={60}>
        <TargetSegments />
      </Reveal>

      <Reveal><div className="grab-divider" /></Reveal>

      {/* ── Design Principles ── */}
      <Reveal><div className="grab-section-label">Product & Design Principles</div></Reveal>
      <Reveal delay={60}>
        <div className="grab-principles-visual">
          {[
            {
              img: "/Pay%20with%20Grab/image%206.png",
              title: "Simple & Flexible",
              points: ["I can pay quickly and easily.", "I can choose how to pay.", "I can choose when to pay."]
            },
            {
              img: "/Pay%20with%20Grab/image%207.png",
              title: "Rewarding",
              points: ["I get rewarded for paying with Grab.", "I am offered with better values.", "I am delighted with the experience."]
            },
            {
              img: "/Pay%20with%20Grab/image%208.png",
              title: "Secure",
              points: ["My money is safe.", "My data is protected.", "My problems are resolved timely."]
            },
          ].map((p, i) => (
            <Reveal key={i} delay={i * 90}>
              <div className="grab-principle-vcol">
                <div className="grab-principle-vimg">
                  <img src={p.img} alt={p.title} />
                </div>
                <div className="grab-principle-vtitle">{p.title}</div>
                <ul className="grab-principle-vlist">
                  {p.points.map((pt, j) => <li key={j}>{pt}</li>)}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </Reveal>

      <Reveal><div className="grab-divider" /></Reveal>

      {/* ── Design: Discovery ── */}
      <Reveal><div className="grab-section-label">Design — Discovery</div></Reveal>
      <Reveal delay={80}>
        <div className="grab-2col-layout">
          <div>
            <h3 className="grab-h3">Onboarding experience</h3>
            <p className="grab-body-text">By highlighting the customer value proposition in a way that is attractive and informative, we can convert more customers from cash to GrabPay as they understand what value we can add to them.</p>
          </div>
          <div />
        </div>
      </Reveal>
      <Reveal>
        <div className="grab-full-img">
          <img src={img(8)} alt="Discovery onboarding" />
        </div>
      </Reveal>

      {/* ── Design: Activation ── */}
      <Reveal><div className="grab-section-label">Design — Activation</div></Reveal>
      <Reveal delay={80}>
        <div className="grab-2col-layout">
          <div>
            <h3 className="grab-h3">Streamlined finance activation experience</h3>
            <p className="grab-body-text">I reduced the process of linking and adding cards, resulting in fewer activation steps. Additionally, I enhanced clarity by providing status updates for each step — indicating when KYC is pending or successful.</p>
            <p className="grab-body-text">Users can now enjoy improved flexibility as they can proceed to the next step while waiting for pending approvals. Based on data, 47% of users only left "link card" 1 step — by offering a clear setup status, users gain the assurance to complete activation within a minute.</p>
          </div>
          <div>
            <h3 className="grab-h3">Key improvements</h3>
            <ul className="grab-info-list">
              <li>Flexibility to sign up in any order — removed dependency on wallet to access other products</li>
              <li>Comprehensive activation status mapping for all possible states</li>
              <li>Scalable architecture for financial services activation</li>
            </ul>
          </div>
        </div>
      </Reveal>
      <Reveal>
        <div className="grab-2col-img">
          <img src={img(9)} alt="Activation flow 1" />
          <img src={img(10)} alt="Activation flow 2" />
        </div>
      </Reveal>
      <Reveal>
        <div className="grab-full-img">
          <img src={img(11)} alt="Activation system" />
        </div>
      </Reveal>

      {/* ── Design: Making Payment ── */}
      <Reveal><div className="grab-section-label">Design — Making Payment</div></Reveal>
      <Reveal delay={80}>
        <div className="grab-2col-layout">
          <div>
            <h3 className="grab-h3">Consistent online / offline / in-app experience</h3>
            <p className="grab-body-text">Giving users awareness of flexible payment options and a consistent payment experience across online and offline channels drives better conversion.</p>
            <p className="grab-body-text">Users can freely choose how & when they want to pay in-app, easily pay for rides, food, mart and deliveries with Grab, and earn rewards for all transactions.</p>
          </div>
          <div>
            <p className="grab-body-text">Flexible payment options including paying now and paying next month with 0% interest and no hidden fees.</p>
            <p className="grab-body-text">Earn rewards for all transactions made at more than 50 million online stores that support GrabPay.</p>
          </div>
        </div>
      </Reveal>
      <Reveal>
        <div className="grab-2col-img">
          <img src={img(12)} alt="Payment screen 1" />
          <img src={img(13)} alt="Payment screen 2" />
        </div>
      </Reveal>
      <Reveal><div className="grab-divider" /></Reveal>

      {/* ── Design System ── */}
      <Reveal><div className="grab-section-label">Payment Design System</div></Reveal>
      <Reveal delay={60}>
        <div className="grab-ds-grid">
          {[
            { label: "Principles", body: "PWG is a set of guiding principles for building high-quality Payments products at Grab and Grab Financial Group." },
            { label: "Components", body: "PWG is a local library of reusable UI components specific to Payments context, built on top of Duxton." },
            { label: "Patterns", body: "PWG is a collection of payments-focused design patterns which serve as recommendations and references." },
          ].map((d, i) => (
            <Reveal key={i} delay={i * 70}>
              <div className="grab-ds-card">
                <div className="grab-ds-label">{d.label}</div>
                <p className="grab-ds-body">{d.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Reveal>
      <Reveal>
        <div className="grab-full-img">
          <img src={img(14)} alt="Design system overview" />
        </div>
      </Reveal>

      <Reveal><div className="grab-divider" /></Reveal>

      {/* ── Results ── */}
      <Reveal><div className="grab-section-label">Result & Outcomes</div></Reveal>
      <Reveal delay={60}>
        <ResultsSection />
      </Reveal>

      <div style={{ height: 80 }} />
    </div>
  )
}
